// RehabEstimator — Phase 1 tier-preset modal + Phase 3 AI scan launcher.
//
// Usage: drop next to any rehab/repair_cost field in an analyzer:
//   <RehabEstimator
//     sqft={form.sqft}
//     state={form.state}
//     leadId={lead?.id}
//     onPick={(val) => setForm((f) => ({...f, repair_cost: val}))}
//   />
// Renders a small "📐 Estimate" button → opens the tier modal. Inside the
// modal, a separate "AI photo scan" tab handles the Pro-tier upsell + the
// 5-photo upload flow.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import UpgradePrompt from "@/components/UpgradePrompt";
import RehabRenderer from "@/components/RehabRenderer";
import {
  Ruler,
  Sparkles,
  X,
  ImagePlus,
  Loader2,
  Wand2,
  Crown,
  Camera,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";

const TIER_KEYS = ["light", "medium", "heavy", "gut"];

export default function RehabEstimator({ sqft, state, leadId, onPick, leadContext }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("quick"); // quick | ai

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="btn-ghost text-xs"
        data-testid="rehab-estimator-open"
        title="Open the rehab estimator"
      >
        <Ruler size={13} /> Estimate
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl p-0 overflow-hidden bg-white">
          <DialogTitle className="sr-only">Rehab Estimator</DialogTitle>
          <DialogDescription className="sr-only">
            Calculate a rehab budget by tier or scan property photos with AI.
          </DialogDescription>

          {/* Tabs */}
          <div className="flex border-b border-[var(--line)] bg-[var(--bg)]">
            <button
              className={`flex-1 px-5 py-3 text-sm font-bold flex items-center justify-center gap-2 ${tab === "quick" ? "bg-white text-[var(--ink)] border-b-2 border-[var(--primary)]" : "text-[var(--ink-2)]"}`}
              onClick={() => setTab("quick")}
              data-testid="rehab-tab-quick"
            >
              <Ruler size={14} /> Quick estimate
            </button>
            <button
              className={`flex-1 px-5 py-3 text-sm font-bold flex items-center justify-center gap-2 relative ${tab === "ai" ? "bg-white text-[var(--ink)] border-b-2 border-[var(--primary)]" : "text-[var(--ink-2)]"}`}
              onClick={() => setTab("ai")}
              data-testid="rehab-tab-ai"
            >
              <Sparkles size={14} className="text-[var(--primary)]" /> AI photo scan
              <span className="text-[9px] font-black text-[var(--primary-dark)] bg-[var(--primary)]/15 px-1.5 py-[1px] rounded uppercase tracking-widest">Pro</span>
            </button>
          </div>

          {tab === "quick" ? (
            <QuickPanel
              sqft={sqft}
              state={state}
              leadId={leadId}
              onPick={(val) => { onPick(val); setOpen(false); }}
            />
          ) : (
            <AIPanel
              sqft={sqft}
              state={state}
              leadId={leadId}
              leadContext={leadContext}
              onPick={(val) => { onPick(val); setOpen(false); }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─── PHASE 1 — Quick tier-preset estimate ────────────────────────────────
function QuickPanel({ sqft: sqftIn, state: stateIn, leadId, onPick }) {
  const [tier, setTier] = useState("medium");
  const [sqft, setSqft] = useState(sqftIn || 1500);
  const [state, setState] = useState(stateIn || "");
  const [meta, setMeta] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/rehab/tiers").then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  const compute = async () => {
    if (!sqft || sqft <= 0) return;
    setLoading(true);
    try {
      const r = await api.post("/rehab/estimate", {
        sqft: Number(sqft), tier, state: state || null,
      });
      setEstimate(r.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Auto-compute on tier/sqft/state changes.
    if (sqft > 0) compute();
  }, [tier, sqft, state]);

  const usd = (n) => n == null ? "—" : `$${Number(n).toLocaleString()}`;

  return (
    <div className="p-6">
      <div className="text-xs uppercase tracking-widest text-[var(--ink-3)] font-bold mb-1">
        Phase 1 · Tier presets
      </div>
      <h2 className="font-display font-bold text-xl mb-4 text-[var(--ink)]">
        Quick rehab estimate by tier.
      </h2>

      {/* Tier picker */}
      <div className="grid grid-cols-4 gap-2 mb-5" data-testid="rehab-tier-picker">
        {TIER_KEYS.map((k) => {
          const m = meta?.tiers?.find((t) => t.key === k);
          const active = tier === k;
          return (
            <button
              key={k}
              type="button"
              onClick={() => setTier(k)}
              className={`text-left rounded-xl border p-3 transition-all ${active ? "bg-[var(--primary)]/10 border-[var(--primary)]" : "bg-white border-[var(--line)] hover:border-[var(--ink-3)]"}`}
              data-testid={`rehab-tier-${k}`}
            >
              <div className={`font-display font-bold text-sm ${active ? "text-[var(--primary-dark)]" : "text-[var(--ink)]"}`}>{m?.label || k}</div>
              <div className="text-[10px] uppercase tracking-widest text-[var(--ink-3)] mt-1">
                ~${m?.psf || "—"}/sqft
              </div>
              <div className="text-[11px] text-[var(--ink-2)] leading-snug mt-1.5 line-clamp-3">
                {m?.desc || ""}
              </div>
            </button>
          );
        })}
      </div>

      {/* sqft + state */}
      <div className="grid grid-cols-2 gap-3 mb-5">
        <div>
          <label className="field-label">Square footage</label>
          <input
            type="number" min="100" max="20000" step="50"
            className="input" value={sqft}
            onChange={(e) => setSqft(Number(e.target.value))}
            data-testid="rehab-sqft-input"
          />
        </div>
        <div>
          <label className="field-label">State (for cost adjustment)</label>
          <input
            type="text" maxLength="2"
            className="input uppercase" value={state}
            onChange={(e) => setState(e.target.value.toUpperCase())}
            placeholder="FL"
            data-testid="rehab-state-input"
          />
        </div>
      </div>

      {/* Result */}
      {estimate && (
        <div className="bg-gradient-to-br from-[var(--primary)]/8 to-white border border-[var(--primary)]/25 rounded-xl p-4 mb-5" data-testid="rehab-estimate-card">
          <div className="text-xs uppercase tracking-widest text-[var(--primary-dark)] font-bold mb-1">
            Estimated rehab budget
          </div>
          <div className="font-display font-black text-4xl tracking-tighter text-[var(--ink)] mb-1">
            {usd(estimate.range_mid)}
          </div>
          <div className="text-sm text-[var(--ink-2)] mb-2">
            Range: <b>{usd(estimate.range_low)}</b> — <b>{usd(estimate.range_high)}</b> · ±15% contingency
          </div>
          <div className="text-[11px] text-[var(--ink-3)]">
            {estimate.sqft.toLocaleString()} sqft × ${estimate.psf_applied.toFixed(0)}/sqft
            {estimate.state ? ` · ${estimate.state} (×${estimate.multiplier})` : " · national baseline"}
          </div>
        </div>
      )}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => estimate && onPick(estimate.range_mid)}
          disabled={!estimate || loading}
          className="btn-money flex-1 disabled:opacity-60"
          data-testid="rehab-use-estimate"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : "Use this number"}
        </button>
      </div>
    </div>
  );
}

// ─── PHASE 3 — AI photo scan ─────────────────────────────────────────────
function AIPanel({ sqft, state, leadId, leadContext, onPick }) {
  const [quota, setQuota] = useState(null);
  const [photos, setPhotos] = useState([]); // {b64, name}
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/rehab/quota").then((r) => setQuota(r.data)).catch(() => {});
  }, []);

  const addPhotos = async (files) => {
    setErr("");
    const arr = Array.from(files || []);
    if (photos.length + arr.length > 15) {
      setErr("Maximum 15 photos per scan.");
      return;
    }
    const next = [];
    for (const f of arr) {
      if (!/^image\/(jpeg|png|webp)$/.test(f.type)) {
        setErr("Photos must be JPEG, PNG, or WEBP.");
        continue;
      }
      if (f.size > 8 * 1024 * 1024) {
        setErr("Each photo must be under 8 MB.");
        continue;
      }
      const b64 = await readAsBase64(f);
      next.push({ b64, name: f.name });
    }
    setPhotos((p) => [...p, ...next]);
  };

  const removePhoto = (i) =>
    setPhotos((p) => p.filter((_, idx) => idx !== i));

  const runScan = async () => {
    if (photos.length === 0) {
      setErr("Add at least one photo.");
      return;
    }
    setScanning(true); setErr("");
    try {
      const r = await api.post("/rehab/ai-scan", {
        lead_id: leadId || null,
        photos_b64: photos.map((p) => p.b64),
        sqft: sqft || null,
        state: state || null,
        ...(leadContext || {}),
      });
      setResult(r.data.result);
      // Refresh quota so the chip updates.
      const q = await api.get("/rehab/quota");
      setQuota(q.data);
    } catch (e) {
      const detail = e?.response?.data?.detail || "AI scan failed.";
      setErr(detail);
    } finally {
      setScanning(false);
    }
  };

  const usd = (n) => n == null ? "—" : `$${Number(n).toLocaleString()}`;

  // Pro-gate teaser if user isn't on Pro+.
  if (quota && !quota.is_pro_or_above) return <ProUpgradeTeaser />;

  return (
    <div className="p-6">
      <div className="flex items-start justify-between gap-3 mb-1">
        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--primary-dark)] font-bold mb-1 flex items-center gap-1.5">
            <Sparkles size={11} /> Phase 3 · AI photo scan
          </div>
          <h2 className="font-display font-bold text-xl text-[var(--ink)]">
            Drop photos. Claude scopes the rehab.
          </h2>
        </div>
        {quota && (
          <div className="text-[10px] uppercase tracking-widest font-bold bg-[var(--primary)]/10 text-[var(--primary-dark)] px-2 py-1 rounded">
            {quota.ai_scans_remaining}/{quota.ai_scans_daily_cap} left today
          </div>
        )}
      </div>
      <p className="text-sm text-[var(--ink-2)] mb-5 leading-relaxed">
        Upload 5-15 phone photos (kitchen, baths, exterior, roof, HVAC, flooring). Photos are
        analyzed once then <b className="text-[var(--ink)]">discarded</b> — we don&apos;t store them.
      </p>

      {!result && (
        <>
          {/* Drag-drop / file picker */}
          <label
            htmlFor="rehab-photo-input"
            className="block border-2 border-dashed border-[var(--line)] rounded-xl bg-[var(--bg)] hover:bg-white hover:border-[var(--primary)]/40 transition-colors p-8 text-center cursor-pointer"
            data-testid="rehab-photo-dropzone"
          >
            <ImagePlus size={28} className="mx-auto text-[var(--ink-3)] mb-2" />
            <div className="font-semibold text-[var(--ink)] text-sm">Tap to add photos</div>
            <div className="text-xs text-[var(--ink-3)] mt-1">JPEG, PNG, or WEBP — up to 15 photos</div>
            <input
              id="rehab-photo-input"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              className="hidden"
              onChange={(e) => addPhotos(e.target.files)}
              data-testid="rehab-photo-input"
            />
          </label>

          {/* Selected photo thumbs */}
          {photos.length > 0 && (
            <div className="mt-4 grid grid-cols-5 gap-2" data-testid="rehab-photo-thumbs">
              {photos.map((p, i) => (
                <div key={i} className="relative aspect-square rounded-md overflow-hidden border border-[var(--line)]">
                  <img src={`data:image/jpeg;base64,${p.b64}`} alt={p.name} className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removePhoto(i)}
                    className="absolute top-1 right-1 bg-black/70 text-white w-5 h-5 rounded-full flex items-center justify-center"
                  >
                    <X size={11} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {err && (
            <div className="mt-4 text-xs bg-rose-50 border border-rose-200 text-rose-800 px-3 py-2 rounded-md" data-testid="rehab-error">
              {err}
            </div>
          )}

          <button
            type="button"
            onClick={runScan}
            disabled={photos.length === 0 || scanning}
            className="btn-money w-full mt-5 disabled:opacity-60"
            data-testid="rehab-run-scan"
          >
            {scanning ? <><Loader2 size={14} className="animate-spin" /> Claude is scanning… (~10-25 sec)</> :
              <><Wand2 size={14} /> Scan with AI ({photos.length} photo{photos.length === 1 ? "" : "s"})</>}
          </button>
        </>
      )}

      {result && <ScanResult result={result} photos={photos} onPick={() => onPick(result.range_mid)} onReset={() => { setResult(null); setPhotos([]); }} />}
    </div>
  );
}

function ScanResult({ result, photos, onPick, onReset }) {
  const [rendererOpen, setRendererOpen] = useState(false);
  const [seedPhoto, setSeedPhoto] = useState(null);
  const usd = (n) => n == null ? "—" : `$${Number(n).toLocaleString()}`;
  const confColor = result.confidence === "high" ? "text-emerald-700 bg-emerald-100" :
                    result.confidence === "medium" ? "text-amber-700 bg-amber-100" : "text-rose-700 bg-rose-100";
  const condBadge = (c) => {
    const cls = c === "good" ? "bg-emerald-100 text-emerald-700" :
                c === "fair" ? "bg-amber-100 text-amber-700" :
                c === "poor" ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-600";
    return <span className={`text-[10px] font-bold uppercase tracking-widest px-1.5 py-[2px] rounded ${cls}`}>{c}</span>;
  };

  return (
    <div data-testid="rehab-scan-result">
      {/* Hero range */}
      <div className="bg-gradient-to-br from-[#0B1220] to-[#0F172A] text-white rounded-xl p-5 mb-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-[var(--primary)] mb-1">
              AI estimate · {result.tier?.toUpperCase()} tier
            </div>
            <div className="font-display font-black text-4xl tracking-tighter">{usd(result.range_mid)}</div>
            <div className="text-sm text-white/70 mt-1">
              Range: <b className="text-white">{usd(result.range_low)}</b> — <b className="text-white">{usd(result.range_high)}</b>
            </div>
          </div>
          <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded ${confColor}`}>
            {result.confidence} confidence
          </span>
        </div>
        {result.tier_reason && (
          <div className="text-xs text-white/65 mt-3 italic border-t border-white/10 pt-3 leading-relaxed">
            {result.tier_reason}
          </div>
        )}
      </div>

      {/* Per-system breakdown */}
      {result.per_system && (
        <div className="mb-4">
          <div className="text-xs uppercase tracking-widest text-[var(--ink-3)] font-bold mb-2">
            Per-system breakdown
          </div>
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(result.per_system).map(([k, v]) => (
              <div key={k} className="bg-white border border-[var(--line)] rounded-lg p-3" data-testid={`rehab-system-${k}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="font-bold text-sm capitalize text-[var(--ink)]">{k}</div>
                  {condBadge(v.condition)}
                </div>
                <div className="font-display font-bold text-[var(--primary-dark)] text-base">{usd(v.est)}</div>
                {v.note && <div className="text-[11px] text-[var(--ink-2)] mt-1 leading-snug line-clamp-2">{v.note}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SOW bullets */}
      {result.sow?.length > 0 && (
        <div className="mb-4">
          <div className="text-xs uppercase tracking-widest text-[var(--ink-3)] font-bold mb-2">
            Scope of work
          </div>
          <ul className="space-y-1.5">
            {result.sow.map((s, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-[var(--ink)]">
                <CheckCircle2 size={13} className="text-[var(--primary-dark)] mt-1 flex-shrink-0" />
                {s}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Missing systems warning */}
      {result.missing_systems?.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 flex items-start gap-2">
          <AlertTriangle size={14} className="text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-amber-900 leading-relaxed">
            <b>Couldn&apos;t assess from photos:</b> {result.missing_systems.join(", ")}.
            Walk the property to verify.
          </div>
        </div>
      )}

      {/* Photo pick strip → launch the AI After-Rehab Renderer with that photo */}
      {photos?.length > 0 && (
        <div className="mb-4 bg-gradient-to-br from-[var(--primary)]/10 to-white border border-[var(--primary)]/30 rounded-lg p-3"
             data-testid="rehab-render-launcher">
          <div className="flex items-start justify-between gap-3 mb-2">
            <div>
              <div className="text-xs uppercase tracking-widest font-bold text-[var(--primary-dark)] flex items-center gap-1.5">
                <Sparkles size={11} /> See it renovated
              </div>
              <div className="text-[11px] text-[var(--ink-2)] mt-0.5">
                Pick any photo — we&apos;ll AI-render the {result.tier || "medium"} rehab &quot;after&quot; look.
              </div>
            </div>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {photos.map((p, i) => (
              <button
                key={i}
                type="button"
                onClick={() => { setSeedPhoto(p.b64); setRendererOpen(true); }}
                className="relative flex-shrink-0 w-16 h-16 rounded-md overflow-hidden border-2 border-[var(--line)] hover:border-[var(--primary)] transition group"
                data-testid={`rehab-render-thumb-${i}`}
                title="Render this photo's after look"
              >
                <img src={`data:image/jpeg;base64,${p.b64}`} alt="" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition flex items-center justify-center">
                  <Sparkles size={16} className="text-white opacity-0 group-hover:opacity-100 transition" />
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={onPick}
          className="btn-money flex-1"
          data-testid="rehab-scan-use"
        >
          Use {usd(result.range_mid)} →
        </button>
        <button
          type="button"
          onClick={onReset}
          className="btn-outline"
          data-testid="rehab-scan-reset"
        >
          Re-scan
        </button>
      </div>

      <RehabRenderer
        open={rendererOpen}
        onClose={() => setRendererOpen(false)}
        initialPhoto={seedPhoto}
        initialTier={result.tier || "medium"}
      />
    </div>
  );
}

function ProUpgradeTeaser() {
  return (
    <div className="p-6" data-testid="rehab-pro-teaser">
      <UpgradePrompt
        feature="AI Rehab Scan"
        headline="Drop photos. Claude scopes the rehab."
        reason="Upload 5-15 phone photos and Claude Sonnet 4.6 scopes the rehab automatically — tier, range, per-system breakdown, scope of work. Pro users get 5 AI scans per day."
        from="ai_scan"
        testid="rehab-pro-prompt"
      />
      <p className="text-[11px] text-[var(--ink-3)] mt-4 text-center">
        The tier-preset calculator stays free on every plan.
      </p>
    </div>
  );
}

async function readAsBase64(file) {
  // Strip the "data:image/jpeg;base64," prefix — the API wants raw b64.
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const s = String(r.result || "");
      const comma = s.indexOf(",");
      resolve(comma >= 0 ? s.slice(comma + 1) : s);
    };
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}
